import clsx from 'clsx'
import Router from 'next/router'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import cartItemsDuck from 'components/cart/cart-items/duck'
import SearchForm from 'components/search/search-form'
import searchModalDuck from 'components/search/search-section/duck'
import {
  closeSearchPanel,
  handleInPageSearchPopState,
  rememberScrollBeforeSearch,
} from 'components/search/utils'
import TopBubbleNavigations from 'components/editorial/editorial-slides/bubble-navigations/top-bubble-navigations/TopBubbleNavigations'
import StateStorage from 'lib/state-storage'
import {
  getSessionStorageOnInit,
  isAppWebView,
} from 'lib/utils/common/commonUtils'
import HeaderModals from './modal-section'
import NavigationMenu from './navigation-menu'
import PomeloLogo from './pomelo-logo'
import styles from './style.scss'
import UserMenu from './user-menu'
import {
  focusSearchBox,
  injectAppAlert,
  segmentSearchIconClick,
  trackViewCartEvent,
} from './utils'

const Header = ({
  applyStoreCredit,
  cart,
  closeSearchModal,
  internationalization: { country, language },
  isPhone,
  isSticky,
  noCountrySelector,
  openResults,
  pageName,
  searchQuery,
  showSearch,
  showSearchModal,
  toggleSearchModal,
  topNavigations,
  updateCart,
}) => {
  const headerRef = useRef(null)
  const hasTopNavigations = Boolean(topNavigations?.length)
  const initialSearchWord = getSessionStorageOnInit('recent-search-word')
  const [searchWord, setSearchWord] = useState(initialSearchWord)
  const [showCountrySelectorModal, setShowCountrySelectorModal] =
    useState(false)
  const isGlobalCountry = country === 'global'

  const openCart = useCallback(() => {
    updateCart({
      cartOpen: true,
      couponUsed: null,
      freeGiftOpen: false,
      isStoreCreditUsed: applyStoreCredit,
    })
    trackViewCartEvent(cart)
  }, [applyStoreCredit, cart, updateCart])

  // open cart again after clicking checkout as a guest and then successfully login
  useEffect(() => {
    const checkoutText = `/${country}/${language}/checkout`
    const successfulLogin = StateStorage.getLocalState('successfullogincart')

    if (successfulLogin === checkoutText) {
      openCart()
      StateStorage.removeLocalState('successfullogincart')
    }
  }, [country, language, openCart])

  // inject app alert class while showing country selector when applicable
  useEffect(() => {
    injectAppAlert()

    if (
      !isAppWebView() &&
      !noCountrySelector &&
      isGlobalCountry &&
      !StateStorage.getCountry()
    ) {
      setShowCountrySelectorModal(true)
    }
  }, [isGlobalCountry, noCountrySelector])

  // TECH-20: Back/Forward over in-page search results opens/closes the panel
  const isResultsOpenRef = useRef(false)
  isResultsOpenRef.current = Boolean(searchQuery)
  // whether search is open in any form (dropdown or results)
  const isSearchOpenRef = useRef(false)
  isSearchOpenRef.current = showSearchModal

  useEffect(() => {
    if (typeof Router.beforePopState !== 'function') {
      return undefined
    }

    Router.beforePopState((state) =>
      handleInPageSearchPopState(state, {
        closeResults: closeSearchModal,
        isResultsOpen: isResultsOpenRef.current,
        openResults,
      }),
    )
    // Any navigation (a nav link, the logo, a product, "see all"…) closes the
    // search so the shopper lands on the page they clicked, nothing over it.
    const closeOnNavigation = () => {
      if (isSearchOpenRef.current) {
        closeSearchModal()
      }
    }
    Router.events.on('routeChangeStart', closeOnNavigation)

    return () => {
      Router.beforePopState(() => true)
      Router.events.off('routeChangeStart', closeOnNavigation)
    }
  }, [closeSearchModal, openResults])

  // TECH-20: clicking anywhere outside the search box and its dropdown closes
  // the search, so clicking a nav link, a home-feed tab or the logo lands the
  // shopper on that view with nothing over it (navigations also fire
  // routeChangeStart above; this also covers in-page tabs that don't route).
  useEffect(() => {
    if (!showSearchModal) {
      return undefined
    }

    const closeIfOutside = (event) => {
      const { target } = event
      if (
        target.closest?.('#header-search-form') ||
        target.closest?.('.search-section__wrapper')
      ) {
        return
      }
      closeSearchModal()
    }

    document.addEventListener('mousedown', closeIfOutside)
    return () => document.removeEventListener('mousedown', closeIfOutside)
  }, [showSearchModal, closeSearchModal])

  const classes = clsx('top-nav header', {
    large: true,
    'search-bar-active': showSearchModal,
    sticky: isSticky,
  })

  const toggleSearch = () => {
    if (searchQuery) {
      closeSearchPanel({ closeSearchModal, query: searchQuery })
      return
    }

    if (headerRef?.current) {
      if (!showSearchModal) {
        rememberScrollBeforeSearch()
      }
      toggleSearchModal()
      segmentSearchIconClick(pageName)

      // wait for the search box to be rendered then focus on it
      setTimeout(() => focusSearchBox(), 1)
    }
  }

  // TECH-20: desktop search box lives in the header, as on ASOS — clicking it
  // opens search in place (page greyed out behind), keeping logo and menu
  const openSearchInPlace = () => {
    if (!showSearchModal) {
      rememberScrollBeforeSearch()
      toggleSearchModal()
      segmentSearchIconClick(pageName)
    }
  }
  const inlineSearch = !isPhone ? (
    <SearchForm
      alwaysShowClose={Boolean(searchQuery)}
      className="header-inline-search"
      onFocus={openSearchInPlace}
      searchWord={searchWord}
      setSearchWord={setSearchWord}
    />
  ) : null

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <header className={classes} ref={headerRef}>
        {hasTopNavigations && !isPhone ? (
          <React.Fragment>
            <div className="header-section header-left">
              <NavigationMenu isSearchBarActive={showSearchModal} />
              <div className="header-logo">
                <PomeloLogo />
              </div>
            </div>
            <div className="header-section header-navigation">
              <TopBubbleNavigations />
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="header-section">
              <NavigationMenu isSearchBarActive={showSearchModal} />
            </div>
            <div className="header-section header-logo">
              <PomeloLogo />
            </div>
          </React.Fragment>
        )}
        <UserMenu
          inlineSearch={inlineSearch}
          openCart={openCart}
          showSearch={showSearch}
          toggleSearch={toggleSearch}
        />
        {isPhone && (
          <div
            className={clsx('header-search__input', {
              active: showSearchModal,
            })}
          >
            <SearchForm
              alwaysShowClose
              searchWord={searchWord}
              setSearchWord={setSearchWord}
            />
          </div>
        )}
      </header>
      {/* TECH-20: on mobile, search is the spyglass icon in the header only
          (no large home search bar); tapping it opens the search overlay.
          Desktop keeps the always-visible inline search box. */}
      <HeaderModals
        searchWord={searchWord}
        setSearchWord={setSearchWord}
        setShowCountrySelectorModal={setShowCountrySelectorModal}
        showCountrySelectorModal={showCountrySelectorModal}
        toggleSearch={toggleSearch}
      />
    </React.Fragment>
  )
}

Header.defaultProps = {
  applyStoreCredit: undefined,
  cart: {},
  isPhone: false,
  isSticky: true,
  noCountrySelector: false,
  pageName: '',
  searchQuery: '',
  showSearch: true,
  showSearchModal: false,
  topNavigations: [],
}

Header.propTypes = {
  applyStoreCredit: PropTypes.bool,
  cart: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id_product: PropTypes.number,
        prices: PropTypes.shape({
          discounted_price: PropTypes.number,
          original_price: PropTypes.number,
        }),
        quantity: PropTypes.number,
      }),
    ),
  }),
  closeSearchModal: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
    language: PropTypes.string,
  }).isRequired,
  isPhone: PropTypes.bool,
  isSticky: PropTypes.bool,
  noCountrySelector: PropTypes.bool,
  openResults: PropTypes.func.isRequired,
  pageName: PropTypes.string,
  searchQuery: PropTypes.string,
  showSearch: PropTypes.bool,
  showSearchModal: PropTypes.bool,
  toggleSearchModal: PropTypes.func.isRequired,
  topNavigations: PropTypes.arrayOf(PropTypes.shape({})),
  updateCart: PropTypes.func.isRequired,
}

export default connect(
  (state) => ({
    applyStoreCredit: state.cartData.isStoreCreditUsed,
    cart: state.cart.payload,
    internationalization: state.internationalization,
    isPhone: state.device.isPhone,
    searchQuery: state.searchModal.query,
    showSearchModal: state.searchModal.showSearchModal,
    topNavigations: state.homePage.topNavigations,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        closeSearchModal: searchModalDuck.creators.closeSearchModal,
        openResults: searchModalDuck.creators.openSearchResults,
        toggleSearchModal: searchModalDuck.creators.toggleSearchModal,
        updateCart: cartItemsDuck.creators.update,
      },
      dispatch,
    ),
)(Header)
